<template>
  <div class="container">
    <div class="row">
      <div class="col-12">
        <div class="box mt-2">
          <div class="row">
            <h3>
              <router-link :to="{ name: 'admin.master-data.index' }">
                {{ $t('masterData.detailTitle') }}
              </router-link>
            </h3>
            <h5>
              {{ tableName }}
            </h5>
          </div>

          <!-- Filter Form -->
          <div class="row mt-3">
            <div class="col-md-3">
              <label class="form-label">{{ $t('masterData.filters.createdDate') }}</label>
              <div class="input-group">
                <input
                  type="date"
                  class="form-control form-control-sm"
                  :placeholder="$t('masterData.filters.filterStart')"
                  v-model="filters.createdFrom"
                >
                <span class="input-group-text">〜</span>
                <input
                  type="date"
                  class="form-control form-control-sm"
                  :placeholder="$t('masterData.filters.filterEnd')"
                  v-model="filters.createdTo"
                >
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label">{{ $t('masterData.filters.updatedDate') }}</label>
              <div class="input-group">
                <input
                  type="date"
                  class="form-control form-control-sm"
                  :placeholder="$t('masterData.filters.filterStart')"
                  v-model="filters.updatedFrom"
                >
                <span class="input-group-text">〜</span>
                <input
                  type="date"
                  class="form-control form-control-sm"
                  :placeholder="$t('masterData.filters.filterEnd')"
                  v-model="filters.updatedTo"
                >
              </div>
            </div>
            <div class="col-md-2">
              <label class="form-label">&nbsp;</label>
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="includeDeleted"
                  v-model="filters.includeDeleted"
                >
                <label class="form-check-label" for="includeDeleted">
                  {{ $t('masterData.filters.toggleIncludeDeleted') }}
                </label>
              </div>
            </div>
            <div class="col-md-2 d-flex align-items-end justify-content-end">
              <button
                type="button"
                class="btn btn-primary"
                @click="applyFilters"
              >
                {{ $t('masterData.filters.searchButton') }}
              </button>
            </div>
          </div>

          <!-- Pagination Controls -->
          <div v-if="!isLoading && !error && totalItems > 0" class="row mt-3 align-items-center">
            <div class="col-md-4">
              <div class="d-flex align-items-center">
                <label class="form-label me-2 mb-0">{{ $t('pagination.itemsPerPage') }}</label>
                <select
                  class="form-select form-select-sm"
                  style="width: auto;"
                  v-model="itemsPerPage"
                  @change="changeItemsPerPage(itemsPerPage)"
                >
                  <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
                    {{ $t('pagination.itemsCount', { count: option }) }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-4 text-center">
              {{ $t('pagination.showing', {
                start: (currentPage - 1) * itemsPerPage + 1,
                end: Math.min(currentPage * itemsPerPage, totalItems),
                total: totalItems
              }) }}
            </div>
            <div class="col-md-4">
              <div class="d-flex align-items-center justify-content-end">
                <label class="form-label me-2 mb-0">{{ $t('pagination.page') }}</label>
                <select
                  class="form-select form-select-sm"
                  style="width: auto;"
                  v-model="currentPage"
                  @change="changePage(currentPage)"
                >
                  <option v-for="page in totalPages" :key="page" :value="page">
                    {{ page }}
                  </option>
                </select>
                <span class="ms-2">{{ $t('pagination.totalPages', { total: totalPages }) }}</span>
              </div>
            </div>
          </div>

          <div v-if="isLoading" class="text-center mt-4">
            {{ $t('masterData.loading') }}
          </div>

          <div v-else-if="error" class="alert alert-danger mt-4">
            {{ error }}
          </div>

          <div v-else-if="totalItems === 0" class="mt-3">
            <!-- Table Data List View -->
            <div class="table-responsive my-4">
              <table class="table table-sm table-list table-hover">
                <thead>
                  <tr>
                    <th v-for="column in columns" :key="column">
                      {{ column }}
                    </th>
                  </tr>
                </thead>
              </table>
            </div>
          </div>

          <div v-else class="table-responsive mt-3">
            <table class="table table-sm table-list table-hover">
              <thead>
                <tr>
                  <th v-for="column in columns" :key="column">
                    {{ column }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in paginatedData" :key="index">
                  <td v-for="column in columns" :key="column">
                    {{ getCellValue(row, column) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';

const route = useRoute();
const tableName = computed(() => route.params.tableName);

const tableData = ref([]);
const columns = ref([]);
const sensitiveColumns = ref([]);
const isLoading = ref(true);
const error = ref('');

const currentPage = ref(1);
const itemsPerPage = ref(50);
const totalItems = ref(0);
const itemsPerPageOptions = [20, 50, 100];

const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return tableData.value.slice(start, end);
});

const filters = ref({
  createdFrom: '',
  createdTo: '',
  updatedFrom: '',
  updatedTo: '',
  includeDeleted: false
});

const fetchTableData = async (filterParams = {}) => {
  try {
    isLoading.value = true;
    error.value = '';

    const params = new URLSearchParams();
    Object.keys(filterParams).forEach(key => {
      if (filterParams[key] !== '' && filterParams[key] !== false) {
        params.append(key, filterParams[key]);
      }
    });

    const url = `/api/admin/master-data/${tableName.value}${params.toString() ? '?' + params.toString() : ''}`;
    const response = await axios.get(url);
    console.log('API Response:', response.data);
    tableData.value = response.data.data;
    columns.value = response.data.columns || [];
    sensitiveColumns.value = response.data.sensitiveColumns || [];
    totalItems.value = tableData.value.length;
  } catch (err) {
    console.error('Error fetching table data:', err);
    if (err.response?.status === 404) {
      error.value = 'Table not found';
    } else {
      error.value = 'Failed to fetch data';
    }
  } finally {
    isLoading.value = false;
  }
};

const applyFilters = () => {
  currentPage.value = 1;
  const filterParams = {};

  if (filters.value.createdFrom) filterParams.created_from = filters.value.createdFrom;
  if (filters.value.createdTo) filterParams.created_to = filters.value.createdTo;
  if (filters.value.updatedFrom) filterParams.updated_from = filters.value.updatedFrom;
  if (filters.value.updatedTo) filterParams.updated_to = filters.value.updatedTo;
  if (filters.value.includeDeleted) filterParams.include_deleted = 'true';

  fetchTableData(filterParams);
};

const changeItemsPerPage = (newItemsPerPage) => {
  itemsPerPage.value = newItemsPerPage;
  currentPage.value = 1;
};

const changePage = (newPage) => {
  currentPage.value = newPage;
};

const getCellValue = (row, column) => {
  if (sensitiveColumns.value.includes(column)) {
    return '***';
  }
  return row[column] || '';
};

onMounted(() => {
  fetchTableData();
});
</script>

<style scoped>
.table td, .table th {
  min-width: 180px;
  white-space: nowrap;
}

/* Filter Form components minimum widths */
.form-control[type="date"] {
  min-width: 70px;
}

.form-check.form-switch {
  min-width: 180px;
}

.btn {
  min-width: 100px;
}
</style>